import React from 'react';
import useSEO from "./useSEO";

const Manufacturing = () => {
  useSEO({
    title: "Manufacturing Services in Hyderabad | Manufacturing Services | Industrial & Production Solutions",
    description:
      "Professional manufacturing services in Hyderabad offering end-to-end industrial production, assembly, and process optimization. We help businesses enhance efficiency, quality, and output across multiple sectors.",
    keywords:
      "manufacturing services Hyderabad, industrial manufacturing Hyderabad, production solutions Hyderabad, factory setup Hyderabad, assembly line services, contract manufacturing Hyderabad, precision engineering Hyderabad, industrial solutions Hyderabad, process automation Hyderabad, mechanical fabrication Hyderabad",
    ogTitle: "Manufacturing Services in Hyderabad - Industrial & Production Experts",
    ogDescription:
      "Expert manufacturing services in Hyderabad for industrial and production needs. We provide efficient, reliable, and quality-driven solutions for your business.",
    ogImage: "imgs/manufacturing-services-process.jpg",
    twitterTitle: "Manufacturing Services in Hyderabad - Industrial & Production Experts",
    twitterDescription:
      "Enhance your production capabilities with our professional manufacturing and industrial solutions in Hyderabad.",
    twitterImage: "imgs/manufacturing-services-process.jpg",
  });

  return (
    <div>
      <>
        {/* Section 1 - Introduction */}
        <section className="pt-lg-8 pt-xl-9">
          <div className="container position-relative pt-4 pt-lg-0">
            <div className="row align-items-center">
              {/* Left Content */}
              <div className="col-md-7 col-xl-6 mb-7 mb-md-0">
                <h1 className="mb-4">Manufacturing Services</h1>
                <p className="mb-4">
                  <b>Manufacturing</b> plays a vital role in transforming raw
                  materials into high-quality products through precision, planning,
                  and process excellence. We specialize in providing scalable and
                  technology-driven manufacturing solutions that cater to diverse
                  industrial sectors including engineering, construction, automotive,
                  electronics, and consumer goods.
                </p>
                <p className="mb-4">
                  Our focus is on delivering efficient production systems, process
                  automation, and end-to-end material management to ensure cost
                  control, consistency, and operational excellence. From plant setup
                  to product assembly, we provide complete support to streamline
                  your production workflow.
                </p>
              </div>

              {/* Right Image */}
              <div className="col-md-5 ms-auto text-center">
                <img
                  src="imgs/manufacturing-services.jpg"
                  alt="Manufacturing Services"
                  className="rounded-3 shadow-sm w-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - Key Services */}
        <section className="overflow-hidden pb-0">
          <div className="container">
            <div className="row align-items-center">
              {/* Image */}
              <div className="col-lg-6 text-center position-relative mb-5 mb-lg-0">
                <div className="pe-lg-6 pe-xl-8">
                  <img
                    src="imgs/manufacturing-services-process.jpg"
                    alt="Manufacturing Process"
                    className="rounded-4 shadow-sm w-100"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="col-lg-6 ms-auto">
                <h2 className="mb-lg-4">Our Manufacturing Capabilities:</h2>
                <hr className="my-4 my-lg-5" />

                <ul className="list-group list-group-borderless">
                  <li className="list-group-item heading-color fw-normal d-flex mb-0">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Industrial Production & Assembly</b>
                  </li>
                  <p>
                    Streamlined production lines and precision assembly processes
                    tailored for multiple industrial segments.
                  </p>

                  <li className="list-group-item heading-color fw-normal d-flex mb-0">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Fabrication & Engineering</b>
                  </li>
                  <p>
                    Advanced metal fabrication, machining, and component
                    engineering for high-performance applications.
                  </p>

                  <li className="list-group-item heading-color fw-normal d-flex mb-0">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Contract & OEM Manufacturing</b>
                  </li>
                  <p>
                    End-to-end contract manufacturing for OEMs and startups,
                    ensuring timely delivery and superior quality standards.
                  </p>

                  <li className="list-group-item heading-color fw-normal d-flex mb-0">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Automation & Process Optimization</b>
                  </li>
                  <p>
                    Implementing automation, robotics, and IoT technologies to
                    improve accuracy, productivity, and consistency.
                  </p>

                  <li className="list-group-item heading-color fw-normal d-flex mb-0">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Quality Control & Inspection</b>
                  </li>
                  <p>
                    Multi-level quality inspection using advanced testing tools to
                    ensure zero-defect manufacturing.
                  </p>

                  <li className="list-group-item heading-color fw-normal d-flex mb-0">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Supply Chain & Logistics Management</b>
                  </li>
                  <p>
                    Coordinated material movement and inventory control for
                    on-time project execution and cost efficiency.
                  </p>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3 - Advantages */}
        <section className="py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="text-primary">Why Choose Our Manufacturing Services</h2>
              <p className="lead">
                Experience reliability, scalability, and innovation in every stage
                of production — from design to delivery.
              </p>
            </div>

            <div className="row text-center">
              <div className="col-md-4 mb-4">
                <i className="bi bi-gear-wide-connected text-success fs-3 mb-2"></i>
                <h5>End-to-End Production</h5>
                <p>
                  Complete manufacturing support including design, prototyping,
                  production, assembly, and quality control.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-graph-up text-success fs-3 mb-2"></i>
                <h5>Efficiency & Cost Control</h5>
                <p>
                  Optimize resources and reduce production costs through lean
                  processes and smart automation.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-shield-check text-success fs-3 mb-2"></i>
                <h5>Quality Assurance</h5>
                <p>
                  Consistent quality through advanced inspection, precision
                  engineering, and continuous improvement systems.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-lightning text-success fs-3 mb-2"></i>
                <h5>Technology Integration</h5>
                <p>
                  Integrating modern tools such as AI, IoT, and robotics for smart
                  manufacturing and real-time monitoring.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-cash-stack text-success fs-3 mb-2"></i>
                <h5>Scalable Operations</h5>
                <p>
                  Flexible manufacturing capacity to handle both small-scale and
                  large-scale production requirements.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-lightbulb text-success fs-3 mb-2"></i>
                <h5>Innovation Driven</h5>
                <p>
                  Focused on continuous innovation to deliver sustainable,
                  high-performing, and energy-efficient manufacturing outcomes.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    </div>
  );
};

export default Manufacturing;
